/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * COMPONENTE: ModalAgendamento
 * VERSÃO:  1.0.0
 * ------------------------------------------------------------
 * Modal para agendar vistoria do IMÓVEL:
 *   ✔ Data/hora + local + responsável
 *   ✔ Backend cancela automaticamente tarefas de CONTATO
 *   ✔ Cria lembrete -24h e follow-up pós-vistoria
 * ============================================================
 */

import React, { useState, useEffect } from 'react';
import DiligenciaService from '../../services/DiligenciaService';

const MEIOS = [
  { value: 'WHATSAPP',   label: 'WhatsApp' },
  { value: 'EMAIL',      label: 'E-mail' },
  { value: 'TELEFONE',   label: 'Telefone' },
  { value: 'PRESENCIAL', label: 'Presencial' },
];

export default function ModalAgendamento({
  aberto,
  onFechar,
  onAgendada,
  ordemId,
  imovelId,
  laudoUuid = null,
  imovelUuid = null,
  enderecoInicial = '',
  processo = '',
}) {
  const [dataHora, setDataHora] = useState('');
  const [local, setLocal] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [observacao, setObservacao] = useState('');
  const [meioConfirmacao, setMeioConfirmacao] = useState('WHATSAPP');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);

  /* Reset */
  useEffect(() => {
    if (!aberto) return;
    const amanha = new Date();
    amanha.setDate(amanha.getDate() + 1);
    amanha.setHours(14, 0, 0, 0);
    const localISO = new Date(amanha.getTime() - amanha.getTimezoneOffset() * 60000)
      .toISOString().slice(0, 16);

    setDataHora(localISO);
    setLocal('');
    setResponsavel('');
    setObservacao('');
    setMeioConfirmacao('WHATSAPP');
    setErro(null);
  }, [aberto]);

  /* ESC fecha */
  useEffect(() => {
    if (!aberto) return;
    const h = e => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [aberto, onFechar]);

  const handleSubmeter = async () => {
    if (!dataHora) { setErro('Data e hora são obrigatórias'); return; }
    if (!imovelId)  { setErro('imovelId não informado ao modal'); return; }

    setLoading(true);
    setErro(null);

    try {
      const dataISO = new Date(dataHora).toISOString();

      const resp = await DiligenciaService.agendarVistoria(
        ordemId,
        imovelId,
        dataISO,
        {
          laudoUuid,
          imovelUuid,
          local: local.trim(),
          responsavel: responsavel.trim(),
          observacao: observacao.trim(),
          meioConfirmacao,
          endereco: enderecoInicial,
          processo,
        }
      );

      onAgendada?.(resp);
      onFechar();
    } catch (e) {
      setErro(e.message || 'Falha ao agendar vistoria');
    } finally {
      setLoading(false);
    }
  };

  if (!aberto) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-[2rem] shadow-2xl overflow-hidden">

        {/* HEADER */}
        <div className="bg-emerald-600 p-6 flex items-center justify-between">
          <div>
            <h3 className="text-white font-black uppercase text-sm tracking-wider">
              Agendar Vistoria
            </h3>
            <p className="text-emerald-100 text-[10px] font-black uppercase tracking-widest mt-1">
              Imóvel #{imovelId}
            </p>
          </div>
          <button
            onClick={onFechar}
            className="text-emerald-100 hover:text-white font-black text-xl"
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">

          {erro && (
            <div className="bg-red-100 text-red-800 border-2 border-red-300 p-3 rounded-xl text-[11px] font-black uppercase">
              ⚠ {erro}
            </div>
          )}

          <div className="bg-amber-50 border-2 border-amber-200 p-3 rounded-xl">
            <p className="text-amber-800 text-[10px] font-black uppercase tracking-wider leading-relaxed">
              ⓘ Ao confirmar, todas as tarefas de contato pendentes deste imóvel serão canceladas automaticamente. Tarefas de verificação e divergência permanecem.
            </p>
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1">
              Data e hora *
            </label>
            <input
              type="datetime-local"
              value={dataHora}
              onChange={e => setDataHora(e.target.value)}
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:border-emerald-600 outline-none"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1">
              Local / Complemento
            </label>
            <input
              type="text"
              value={local}
              onChange={e => setLocal(e.target.value)}
              placeholder="Ex: Apto 45, Bloco B — falar com zelador"
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:border-emerald-600 outline-none"
              maxLength={200}
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1">
              Responsável no local
            </label>
            <input
              type="text"
              value={responsavel}
              onChange={e => setResponsavel(e.target.value)}
              placeholder="Nome e telefone do contato"
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:border-emerald-600 outline-none"
              maxLength={150}
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1">
              Meio de confirmação
            </label>
            <select
              value={meioConfirmacao}
              onChange={e => setMeioConfirmacao(e.target.value)}
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:border-emerald-600 outline-none"
            >
              {MEIOS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1">
              Observação
            </label>
            <textarea
              value={observacao}
              onChange={e => setObservacao(e.target.value)}
              placeholder="Informações extras sobre a vistoria"
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:border-emerald-600 outline-none min-h-[70px]"
              maxLength={500}
            />
          </div>

        </div>

        {/* FOOTER */}
        <div className="p-6 bg-slate-50 border-t-2 border-slate-200 flex items-center justify-end gap-3">
          <button
            onClick={onFechar}
            disabled={loading}
            className="px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-wider text-slate-600 hover:bg-slate-200 transition-all disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmeter}
            disabled={loading || !dataHora}
            className="bg-emerald-600 text-white px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-wider hover:bg-emerald-700 transition-all disabled:opacity-50"
          >
            {loading ? 'Agendando...' : 'Confirmar Agendamento'}
          </button>
        </div>

      </div>
    </div>
  );
}